import { useState } from "react";
import { Play } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

export const formatEpisodeDate = (date, lang) => {
  if (!date || date.length === 4) return date;
  const d = new Date(`${date}T12:00:00`);
  return d.toLocaleDateString(lang === "es" ? "es" : "en", { year: "numeric", month: "long", day: "numeric" });
};

export const EpisodeCard = ({ ep, dark = false }) => {
  const { t, lang } = useLang();
  const [playing, setPlaying] = useState(false);
  const info = t.episodes[ep.id];
  const label = `${t.podcastPage.episodeWord} ${ep.num}`;

  return (
    <div
      data-testid={`episode-card-${ep.id}`}
      className={`rounded-3xl overflow-hidden transition-[transform,box-shadow] duration-300 hover:-translate-y-1 ${
        dark
          ? "bg-white/5 border border-white/10 shadow-[0_24px_60px_-30px_rgba(0,0,0,0.6)]"
          : "bg-white border border-border shadow-[0_16px_40px_-24px_rgba(44,37,32,0.25)]"
      }`}
    >
      <div className="relative aspect-video bg-espresso">
        {playing ? (
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${ep.videoId}?autoplay=1&rel=0`}
            title={`${label}: ${info.title}`}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
            className="absolute inset-0 w-full h-full"
          />
        ) : (
          <button
            data-testid={`episode-play-${ep.id}`}
            onClick={() => setPlaying(true)}
            aria-label={`${t.podcastPage.play}: ${label}`}
            className="group absolute inset-0 w-full h-full"
          >
            <img
              src={`https://i.ytimg.com/vi/${ep.videoId}/hqdefault.jpg`}
              alt={`${label}: ${info.title}`}
              loading="lazy"
              className="w-full h-full object-cover opacity-90 transition-[opacity,transform] duration-500 group-hover:opacity-100 group-hover:scale-[1.03]"
            />
            <span className="absolute inset-0 bg-gradient-to-t from-espresso/60 via-transparent to-transparent" aria-hidden="true" />
            <span className="absolute inset-0 flex items-center justify-center" aria-hidden="true">
              <span className="w-14 h-14 rounded-full bg-terracotta/95 flex items-center justify-center shadow-[0_12px_30px_-10px_rgba(200,109,81,0.8)] transition-transform duration-300 group-hover:scale-110">
                <Play className="w-6 h-6 text-linen ml-0.5" fill="currentColor" />
              </span>
            </span>
          </button>
        )}
      </div>
      <div className="p-6">
        <p className={`text-xs uppercase tracking-[0.18em] font-semibold ${dark ? "text-[#E0A98F]" : "text-terracotta"}`}>
          {label} · {formatEpisodeDate(ep.date, lang)}
        </p>
        <h3 className={`font-serif text-xl font-semibold mt-2 ${dark ? "text-linen" : "text-espresso"}`}>{info.title}</h3>
        <p className={`mt-2 text-sm leading-relaxed ${dark ? "text-linen/65" : "text-clay"}`}>{info.desc}</p>
        <a
          href={`https://www.youtube.com/watch?v=${ep.videoId}`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`episode-watch-link-${ep.id}`}
          className={`mt-4 inline-flex items-center gap-1.5 text-sm font-semibold transition-colors ${
            dark ? "text-[#E0A98F] hover:text-linen" : "text-terracotta hover:text-terracotta-dark"
          }`}
        >
          <Play className="w-3.5 h-3.5" /> {t.podcastPage.watchOn}
        </a>
      </div>
    </div>
  );
};
